import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useExpenses } from '../contexts/ExpenseContext';
import { useMode } from '../contexts/ModeContext';
import { useAuth } from '../contexts/AuthContext';
import * as cloudApi from '../services/api';
import { CATEGORIES, FAMILY_CATEGORIES, MONTHS } from '../types';
import { 
  FileText, 
  Camera, 
  ArrowRight,
  Loader2,
  HardDrive,
  TrendingUp,
  TrendingDown,
  Wallet,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Calendar
} from 'lucide-react';

// Categories that count as INCOME (money coming in)
const INCOME_CATEGORIES = ['settlements'];

// Categories that count as EXPENSES (money going out)  
const EXPENSE_CATEGORIES = ['travel', 'fuel', 'meals', 'office', 'miscellaneous'];

// Tax year status options
type TaxYearStatus = 'organizing' | 'ready' | 'filed' | 'paid';

export default function Dashboard() {
  const { expenses, stats, isLoading } = useExpenses();
  const { isFamily } = useMode();
  const { isAuthenticated } = useAuth();

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  
  const [selectedYear, setSelectedYear] = useState(currentYear);
  // Tax years from DB (DISTINCT year) so 2024, 2023, and earlier are selectable
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  useEffect(() => {
    if (!isAuthenticated) return;
    cloudApi.getExpenseYears().then((years) => {
      const set = new Set(years);
      set.add(currentYear);
      setAvailableYears(Array.from(set).sort((a, b) => b - a));
    });
  }, [isAuthenticated, currentYear]);

  // Filter for selected year
  const thisYearExpenses = useMemo(() => 
    expenses.filter(e => e.year === selectedYear), 
    [expenses, selectedYear]
  );
  
  // Filter for current month (only if viewing current year)
  const thisMonthExpenses = useMemo(() => 
    selectedYear === currentYear 
      ? expenses.filter(e => e.month === currentMonth && e.year === selectedYear)
      : [],
    [expenses, selectedYear, currentYear, currentMonth]
  );
  
  // Get document counts per year for the year selector
  const yearCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    availableYears.forEach(y => {
      counts[y] = expenses.filter(e => e.year === y).length;
    });
    return counts;
  }, [expenses, availableYears]);

  // Calculate INCOME (settlements)
  const yearlyIncome = thisYearExpenses
    .filter(e => INCOME_CATEGORIES.includes(e.category))
    .reduce((sum, e) => sum + (e.amount || 0), 0);

  const monthlyIncome = thisMonthExpenses
    .filter(e => INCOME_CATEGORIES.includes(e.category))
    .reduce((sum, e) => sum + (e.amount || 0), 0);

  // Calculate EXPENSES (travel, fuel, meals, etc.)
  const yearlyExpenses = thisYearExpenses
    .filter(e => EXPENSE_CATEGORIES.includes(e.category))
    .reduce((sum, e) => sum + (e.amount || 0), 0);

  const monthlyExpenses = thisMonthExpenses
    .filter(e => EXPENSE_CATEGORIES.includes(e.category))
    .reduce((sum, e) => sum + (e.amount || 0), 0);

  // Calculate NET PROFIT
  const yearlyNetProfit = yearlyIncome - yearlyExpenses;

  const visibleCategories = isFamily ? FAMILY_CATEGORIES : CATEGORIES;
  const categoryYearlyTotals = visibleCategories.reduce((acc, cat) => {
    const categoryExpenses = thisYearExpenses.filter(e => e.category === cat.value);
    acc[cat.value] = {
      count: categoryExpenses.length,
      amount: categoryExpenses.reduce((sum, e) => sum + (e.amount || 0), 0)
    };
    return acc;
  }, {} as Record<string, { count: number; amount: number }>);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Tax Year Selector */}
      <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 backdrop-blur-md rounded-2xl p-4 border border-amber-500/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-amber-400" />
            <div>
              <h2 className="text-lg font-bold text-white">Tax Year</h2>
              <p className="text-xs text-white/60">
                {selectedYear === currentYear ? 'Current year' : selectedYear < currentYear ? '⚠️ Back taxes' : ''}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedYear(Math.min(selectedYear + 1, currentYear))}
              disabled={selectedYear >= currentYear}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-4 py-2 bg-shuttle-dark border-2 border-amber-500/50 rounded-xl text-white text-xl font-bold focus:ring-2 focus:ring-amber-500 transition-all min-w-[120px] text-center"
            >
              {availableYears.map(year => (
                <option key={year} value={year}>
                  {year} {yearCounts[year] > 0 ? `(${yearCounts[year]})` : ''}
                </option>
              ))}
            </select>
            
            <button
              onClick={() => setSelectedYear(Math.max(selectedYear - 1, Math.min(...availableYears, currentYear)))}
              disabled={availableYears.length === 0 || selectedYear <= Math.min(...availableYears)}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
        
        {selectedYear < currentYear && (
          <div className="mt-3 p-3 bg-orange-500/20 rounded-lg border border-orange-500/30">
            <p className="text-sm text-orange-200">
              📋 <strong>Working on {selectedYear} back taxes.</strong> Import receipts and settlements for this year to get organized.
            </p>
          </div>
        )}
      </div>

      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">
            {isFamily ? `${selectedYear} Household Ledger` : `${selectedYear} Tax Documents`}
          </h1>
          <p className="text-shuttle-sky mt-1">
            {selectedYear === currentYear 
              ? `${MONTHS[currentMonth - 1]} ${currentYear} • ${thisMonthExpenses.length} documents this month`
              : `${thisYearExpenses.length} documents for ${selectedYear}`
            }
          </p>
        </div>
        <Link
          to="/scanner"
          className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-xl font-medium hover:from-amber-400 hover:to-amber-500 transition-all shadow-lg"
        >
          <Camera className="w-5 h-5" />
          <span>Scan Receipt</span>
        </Link>
      </div>

      {/* Year-to-Date Financial Summary */}
      <div className="bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Wallet className="w-5 h-5" />
          {selectedYear} Financial Summary
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Income */}
          <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-green-300/70 uppercase tracking-wide">{isFamily ? 'Income' : 'Income (Settlements)'}</p>
                <p className="text-2xl font-bold text-green-400">
                  ${yearlyIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>

          {/* Expenses */}
          <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-xs text-red-300/70 uppercase tracking-wide">Expenses (Deductible)</p>
                <p className="text-2xl font-bold text-red-400">
                  -${yearlyExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>

          {/* Net Profit / Monthly Surplus */}
          <div className={`${yearlyNetProfit >= 0 ? 'bg-blue-500/10 border-blue-500/30' : 'bg-orange-500/10 border-orange-500/30'} rounded-xl p-4 border`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${yearlyNetProfit >= 0 ? 'bg-blue-500/20' : 'bg-orange-500/20'} rounded-lg flex items-center justify-center`}>
                <Wallet className={`w-5 h-5 ${yearlyNetProfit >= 0 ? 'text-blue-400' : 'text-orange-400'}`} />
              </div>
              <div>
                <p className={`text-xs ${yearlyNetProfit >= 0 ? 'text-blue-300/70' : 'text-orange-300/70'} uppercase tracking-wide`}>{isFamily ? 'Surplus' : 'Net Profit (Taxable)'}</p>
                <p className={`text-2xl font-bold ${yearlyNetProfit >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
                  ${yearlyNetProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards - Different for current year vs past years */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <CalendarDays className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">
                {selectedYear === currentYear ? thisMonthExpenses.length : thisYearExpenses.length}
              </p>
              <p className="text-sm text-white/60">
                {selectedYear === currentYear ? `${MONTHS[currentMonth - 1]} Docs` : `${selectedYear} Total Docs`}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-400">
                ${selectedYear === currentYear 
                  ? monthlyIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  : yearlyIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                }
              </p>
              <p className="text-sm text-white/60">
                {selectedYear === currentYear ? `${MONTHS[currentMonth - 1]} Income` : `${selectedYear} Income`}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-400">
                -${selectedYear === currentYear 
                  ? monthlyExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  : yearlyExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                }
              </p>
              <p className="text-sm text-white/60">
                {selectedYear === currentYear ? `${MONTHS[currentMonth - 1]} Expenses` : `${selectedYear} Expenses`}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
              <HardDrive className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats?.storageUsedFormatted || '0 KB'}</p>
              <p className="text-sm text-white/60">Storage Used</p>
            </div>
          </div>
        </div>

        {(stats?.needsReviewCount ?? 0) > 0 && (
          <Link to="/import" className="bg-amber-500/10 backdrop-blur-md rounded-2xl p-6 border border-amber-500/30 hover:border-amber-500/50 transition-colors block">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-400">{stats?.needsReviewCount ?? 0}</p>
                <p className="text-sm text-white/60">Needs Review</p>
              </div>
            </div>
          </Link>
        )}
      </div>

      {/* Quick Actions – hide professional (Import PDF Settlements, Send BOL) when Home profile */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Link
          to="/scanner"
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group"
        >
          <Camera className="w-8 h-8 text-shuttle-sand mb-3" />
          <p className="font-medium text-white">Scan Receipt</p>
          <p className="text-sm text-white/60">Use camera</p>
        </Link>

        {!isFamily && (
          <Link
            to="/import"
            className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group"
          >
            <span className="text-3xl mb-2 block">📄</span>
            <p className="font-medium text-white">Import PDF</p>
            <p className="text-sm text-white/60">Settlements</p>
          </Link>
        )}

        {!isFamily && (
          <Link
            to="/bol-send"
            className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group"
          >
            <span className="text-3xl mb-2 block">📋</span>
            <p className="font-medium text-white">Send BOL</p>
            <p className="text-sm text-white/60">Email to Bennett</p>
          </Link>
        )}

        <Link
          to="/export"
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group"
        >
          <span className="text-3xl mb-2 block">📦</span>
          <p className="font-medium text-white">Export PDF</p>
          <p className="text-sm text-white/60">For CPA</p>
        </Link>

        <a
          href="https://drive.google.com/drive/folders/1UqVIgsKRxmN9-U_SEk11RzwgpdIxQK1S?usp=drive_link"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group"
        >
          <span className="text-3xl mb-2 block">📁</span>
          <p className="font-medium text-white">My Drive</p>
          <p className="text-sm text-white/60">Google Docs</p>
        </a>

        <a
          href="https://drive.google.com/drive/folders/18PDelHUeoq6KJVzkzEIPl-NzCs84diqs?usp=drive_link"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all group"
        >
          <span className="text-3xl mb-2 block">🗂️</span>
          <p className="font-medium text-white">Tracker Docs</p>
          <p className="text-sm text-white/60">Google Drive</p>
        </a>
      </div>

      {/* Year-to-Date by Category */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-white">{selectedYear} by Category</h2>
            <p className="text-xs text-white/50">{selectedYear === currentYear ? 'Year-to-date totals' : `Full year ${selectedYear}`}</p>
          </div>
          <Link 
            to="/expenses" 
            className="text-sm text-shuttle-sky hover:text-white flex items-center space-x-1"
          >
            <span>View all</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {visibleCategories.map((cat) => {
            const yearData = categoryYearlyTotals[cat.value] || { count: 0, amount: 0 };
            const isIncome = !isFamily && INCOME_CATEGORIES.includes(cat.value);
            return (
              <Link
                key={cat.value}
                to={`/expenses?category=${cat.value}`}
                className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 hover:bg-white/20 transition-all group"
              >
                <div className="text-3xl mb-2">{cat.icon}</div>
                <p className="text-2xl font-bold text-white">{yearData.count}</p>
                <p className="text-sm text-white/60 truncate">{cat.label}</p>
                {yearData.amount > 0 && (
                  <p className={`text-sm font-medium mt-1 ${isIncome ? 'text-green-400' : 'text-red-400'}`}>
                    {isIncome ? '+' : '-'}${yearData.amount.toFixed(2)}
                  </p>
                )}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent Documents */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">Recent Documents</h2>
        </div>
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
          {expenses.length > 0 ? (
            <div className="divide-y divide-white/10">
              {expenses.slice(0, 5).map((expense) => {
                const category = CATEGORIES.find(c => c.value === expense.category) ?? FAMILY_CATEGORIES.find(c => c.value === expense.category);
                const isIncome = !isFamily && INCOME_CATEGORIES.includes(expense.category);
                return (
                  <div
                    key={expense.id}
                    className="flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <span className="text-2xl flex-shrink-0">{category?.icon || '📄'}</span>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-white truncate text-sm">
                          {expense.filename}
                        </p>
                        <p className="text-xs text-white/60 truncate">
                          {expense.description || category?.label} • {new Date(expense.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <span className={`category-badge category-${expense.category} text-xs`}>
                        {isIncome ? 'Income' : category?.label}
                      </span>
                      {expense.amount && (
                        <span className={`font-medium text-sm ${isIncome ? 'text-green-400' : 'text-red-400'}`}>
                          {isIncome ? '+' : '-'}${expense.amount.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-12 text-center text-white/60">
              <FileText className="w-12 h-12 mx-auto mb-4 text-white/30" />
              <p>No documents yet</p>
              <Link
                to="/scanner"
                className="inline-flex items-center space-x-2 mt-4 text-shuttle-sky hover:text-white"
              >
                <Camera className="w-4 h-4" />
                <span>Scan your first receipt</span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
